import React from 'react';
import { Recipe } from '../../types/Recipe';
import { Link } from 'react-router-dom';

interface RecipeCardProps {
  recipe: Recipe;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClick?: (id: string) => void;
}

const RecipeCard: React.FC<RecipeCardProps> = ({
  recipe,
  onEdit,
  onDelete,
  onClick
}) => {
  const handleClick = () => {
    if (onClick) onClick(recipe.id);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) onEdit(recipe.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) onDelete(recipe.id);
  };

  // Fonction pour obtenir la couleur de badge selon la catégorie
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'entrée':
        return 'bg-blue-100 text-blue-800';
      case 'plat':
        return 'bg-green-100 text-green-800';
      case 'dessert':
        return 'bg-pink-100 text-pink-800';
      case 'boisson':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction pour obtenir les icônes de difficulté
  const getDifficultyIcons = (difficulty: string) => {
    switch (difficulty) {
      case 'facile':
        return (
          <div className="flex items-center text-green-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="ml-1 text-xs">Facile</span>
          </div>
        );
      case 'moyen':
        return (
          <div className="flex items-center text-yellow-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="ml-1 text-xs">Moyen</span>
          </div>
        );
      case 'difficile':
        return (
          <div className="flex items-center text-red-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="ml-1 text-xs">Difficile</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className="card bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-xl cursor-pointer"
      onClick={handleClick}
      data-aos="fade-up"
    >
      {/* Image de la recette */}
      <div className="relative h-48 overflow-hidden">
        {recipe.imageUrl ? (
          <img 
            src={recipe.imageUrl} 
            alt={recipe.title} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        
        {/* Badge de catégorie */}
        <span className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(recipe.category)}`}>
          {recipe.category}
        </span>
      </div>
      
      {/* Contenu de la carte */}
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-2 text-dark">{recipe.title}</h3>
        
        <div className="flex justify-between items-center mb-3">
          {/* Temps de préparation */}
          <div className="flex items-center text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="ml-1 text-xs">{recipe.prepTime + recipe.cookTime} min</span>
          </div>
          
          {/* Difficulté */}
          {getDifficultyIcons(recipe.difficulty)}
        </div>
        
        {/* Aperçu des ingrédients */}
        {recipe.ingredients.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-1">Ingrédients principaux:</p>
            <div className="flex flex-wrap gap-1">
              {recipe.ingredients.slice(0, 3).map((ingredient, index) => (
                <span key={index} className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                  {ingredient}
                </span>
              ))}
              {recipe.ingredients.length > 3 && (
                <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                  +{recipe.ingredients.length - 3}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Pied de carte avec actions */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
        <Link 
          to={`/recipe/${recipe.id}`}
          className="text-sm text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          Voir détails
        </Link>
        
        <div className="flex space-x-2">
          {onEdit && (
            <Link 
              to={`/edit-recipe/${recipe.id}`}
              className="p-1 text-gray-500 hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="Modifier"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </Link>
          )}
          
          {onDelete && (
            <button 
              className="p-1 text-gray-500 hover:text-red-500 transition-colors"
              onClick={handleDelete}
              aria-label="Supprimer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeCard;
